'use client';

export interface RetailStore {
  id: string;
  name: string;
  address: string;
  city: string;
  pincode: string;
  phone: string;
}

const STORAGE_KEY = 'kasturi_retailer_locations';

const DEFAULT_STORES: RetailStore[] = [
  { id: '1', name: 'Aryan General Store', address: 'Unit-4, Bhubaneswar', city: 'Bhubaneswar', pincode: '751001', phone: '9861000001' },
  { id: '2', name: 'Subhalaxmi Provision Store', address: 'Saheed Nagar, Bhubaneswar', city: 'Bhubaneswar', pincode: '751007', phone: '9861000002' },
  { id: '3', name: 'Mahalaxmi Kirana', address: 'Chandrasekharpur, Bhubaneswar', city: 'Bhubaneswar', pincode: '751016', phone: '9861000003' },
  { id: '4', name: 'Jagannath Stores', address: 'Station Road, Cuttack', city: 'Cuttack', pincode: '753001', phone: '9861000004' },
  { id: '5', name: 'Odisha Grocery Hub', address: 'Buxi Bazar, Cuttack', city: 'Cuttack', pincode: '753001', phone: '9861000005' },
  { id: '6', name: 'Ratha General Merchants', address: 'Berhampur Main Road', city: 'Berhampur', pincode: '760001', phone: '9861000006' },
  { id: '7', name: 'Sambalpur Provision Centre', address: 'Ainthapali, Sambalpur', city: 'Sambalpur', pincode: '768004', phone: '9861000007' },
  { id: '8', name: 'Puri Kirana Bhandar', address: 'Grand Road, Puri', city: 'Puri', pincode: '752001', phone: '9861000008' },
];

export function getRetailers(): RetailStore[] {
  if (typeof window === 'undefined') return DEFAULT_STORES;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_STORES));
      return DEFAULT_STORES;
    }
    return JSON.parse(raw) as RetailStore[];
  } catch {
    return DEFAULT_STORES;
  }
}

export function saveRetailers(stores: RetailStore[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stores));
}

export function addRetailer(store: Omit<RetailStore, 'id'>): RetailStore[] {
  const stores = getRetailers();
  const newStore: RetailStore = { ...store, id: Date.now().toString() };
  const updated = [...stores, newStore];
  saveRetailers(updated);
  return updated;
}

export function updateRetailer(id: string, data: Omit<RetailStore, 'id'>): RetailStore[] {
  const stores = getRetailers();
  const updated = stores.map((s) => (s.id === id ? { ...data, id } : s));
  saveRetailers(updated);
  return updated;
}

export function deleteRetailer(id: string): RetailStore[] {
  const stores = getRetailers();
  const updated = stores.filter((s) => s.id !== id);
  saveRetailers(updated);
  return updated;
}

export function importRetailersFromCSV(csvText: string): { stores: RetailStore[]; errors: string[] } {
  const lines = csvText.trim().split('\n');
  const errors: string[] = [];
  const newStores: RetailStore[] = [];

  // Skip header row if present
  const startIdx = lines[0]?.toLowerCase().includes('name') ? 1 : 0;

  for (let i = startIdx; i < lines.length; i++) {
    const cols = lines[i].split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    const [name, address, city, pincode, phone] = cols;
    if (!name || !address || !city || !pincode) {
      errors.push(`Row ${i + 1}: Missing required fields (name, address, city, pincode)`);
      continue;
    }
    if (!/^\d{6}$/.test(pincode)) {
      errors.push(`Row ${i + 1}: Invalid PIN code "${pincode}" — must be 6 digits`);
      continue;
    }
    newStores.push({ id: Date.now().toString() + i, name, address, city, pincode, phone: phone || '' });
  }

  if (newStores.length > 0) {
    const existing = getRetailers();
    const merged = [...existing, ...newStores];
    saveRetailers(merged);
    return { stores: merged, errors };
  }

  return { stores: getRetailers(), errors };
}
